// File: FamilyAssistanceApplication.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function FamilyAssistanceApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // --- Core form state ---
  const [form, setForm] = useState({
    // Which program(s)
    applyFamiliesFirst: false,
    applySNAP: false,

    // Applicant basics
    fullName: '',
    primaryLanguage: '',
    email: '',
    homePhone: '',
    workPhone: '',
    cellPhone: '',
    okToLeaveMsg: true,

    // Address (home)
    homeStreet: '',
    homeCity: '',
    homeState: '',
    homeZip: '',
    homeless: 'No',         // Yes | No
    needEBT: 'No',          // Yes | No

    // Mailing address (optional)
    mailingStreet: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',

    // Demographics
    maritalStatus: '',      // married | single | divorced | widowed | separated
    raceWhite: false,
    raceBlack: false,
    raceAsian: false,
    raceHawaiian: false,
    raceNative: false,

    // Books from Birth
    declineBooksFromBirth: false,

    // Families First only (Child Support compliance + school attendance permission)
    willComplyChildSupport: '',
    consentSchoolAttendance: false,

    // Copy preferences
    wantCopy: 'No',         // Yes | No
    copyType: 'Electronic', // Paper | Electronic

    // Authorized reps (up to two)
    rep1Name: '',
    rep1MayApplySNAP: false,
    rep1MayApplyFF: false,
    rep1MayUseSNAP: false,
    rep1MayUseFF: false,

    rep2Name: '',
    rep2MayApplySNAP: false,
    rep2MayApplyFF: false,
    rep2MayUseSNAP: false,
    rep2MayUseFF: false,

    // Voter Registration
    vrRegistered: '',
    vrWouldLikeToRegister: '',
    vrMailForm: '',

    // Disqualifications / convictions (Yes/No)
    dvTwoStatesSameTime: '',
    dvTANFRestrictedLocations: '',
    dvSellSNAPOver500: '',
    dvDrugFelonyPost1996: '',
    dvFleeingFelonParoleProbation: '',
    dvTradeSNAPForDrugs: '',
    dvReceivedSnapTanfOtherState30Days: '',
    dvAge18to24InStateCustody: '',

    // Expenses
    childCareAmountWeekly: '',
    childCareProvider: '',
    shelterRentOrMortgage: '',
    shelterGasElectricMonthly: '',
    shelterPhoneMonthly: '',

    // Medical recurring (captured in dynamic list below, but keep a couple of easy fields if needed)
    // Child support paid (dynamic list below)

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  // Dynamic lists
  const [incomeRows, setIncomeRows] = useState([
    { person: '', source: '', monthlyAmount: '' }
  ]);
  const [resourceRows, setResourceRows] = useState([
    { type: '', value: '' }
  ]);
  const [medicalRows, setMedicalRows] = useState([
    { type: '', monthlyAmount: '' }
  ]);
  const [childSupportRows, setChildSupportRows] = useState([
    { child: '', monthlyAmount: '' }
  ]);

  // --- Helpers ---
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }
    if (name.toLowerCase().endsWith('state')) {
      setForm((f) => ({ ...f, [name]: value.toUpperCase() }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const updateRow = (rows, setRows, idx, key, value) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: value };
    setRows(copy);
  };
  const addRow = (rows, setRows, blankObj) => setRows([...rows, { ...blankObj }]);
  const removeRow = (rows, setRows, idx) => {
    const copy = [...rows];
    copy.splice(idx, 1);
    setRows(copy.length ? copy : [{}]); // keep at least one
  };

  // --- Validation ---
  const validate = () => {
    const e = {};
    // Only require the first field (fullName) for demo purposes
    if (!String(form.fullName).trim()) {
      e.fullName = 'Required';
    }

    // Keep format validation for fields that are filled in (but don't require them)
    if (form.homeState && !isTwoLetterState(form.homeState)) e.homeState = 'Use 2-letter code (e.g., TN)';
    if (form.mailingState && form.mailingState.trim() && !isTwoLetterState(form.mailingState)) e.mailingState = 'Use 2-letter code (e.g., TN)';

    if (form.homeZip && !isFiveZip(form.homeZip)) e.homeZip = '5-digit ZIP';
    if (form.mailingZip && form.mailingZip.trim() && !isFiveZip(form.mailingZip)) e.mailingZip = '5-digit ZIP';

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';

    if (form.homePhone && !isTenPhone(form.homePhone)) e.homePhone = '10 digits';
    if (form.workPhone && !isTenPhone(form.workPhone)) e.workPhone = '10 digits';
    if (form.cellPhone && !isTenPhone(form.cellPhone)) e.cellPhone = '10 digits';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true); // show “Congrats/submitted” screen
    }, 500);
  };

  const resetForm = () => window.location.reload();

  // --- UI ---
  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            {/* Header / Actions */}
            <div className="department-details-content-box">
              <h4 className="department-details-title">Family Assistance Application (SNAP &amp; Families First)</h4>
              <p className="gts-form-overview">
                Apply for benefits with the State of GTS Family &amp; Children Services. You can download the official PDF or
                complete the compact web form below. This demo shows a <strong>dummy submission</strong>—no data is stored or transmitted.
              </p>
              <div className="gts-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                {/* Update the href to the final public path for the PDF in your app bundle */}
                <a 
                  href="/guides/State of GTS Family Assistance Application 2025V1.pdf" 
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Application (PDF)
                </a>
                <Link 
                  to="/department/family-services/family-support"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#666',
                    textDecoration: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Family Assistance Info
                </Link>
              </div>
            </div>

            {/* Success screen */}
            {submitted ? (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                  <button 
                    onClick={() => window.print()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#666',
                      textDecoration: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Print this page
                  </button>
                  <a 
                    href="/guides/StateOfGTS-FamilyAssistanceApplication-2025V1.pdf" 
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download Application (PDF)
                  </a>
                  <Link 
                    to="/forms"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#666',
                      textDecoration: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Back to Forms &amp; Applications
                  </Link>
                </div>
              </div>
            ) : (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* Programs */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Programs</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="applySNAP" checked={form.applySNAP} onChange={onChange} />
                      <span>Apply for SNAP</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="applyFamiliesFirst" checked={form.applyFamiliesFirst} onChange={onChange} />
                      <span>Apply for Families First (cash)</span>
                    </label>
                    {errors.programs && <em className="gts-error">{errors.programs}</em>}
                  </div>
                </div>

                {/* Applicant & Contact */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Applicant &amp; Contact</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2">
                      <span>Full Name <span className="gts-required">*</span></span>
                      <input name="fullName" value={form.fullName} onChange={onChange}/>
                      {errors.fullName && <em className="gts-error">{errors.fullName}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Primary/Preferred Language</span>
                      <input name="primaryLanguage" value={form.primaryLanguage} onChange={onChange}/>
                    </label>

                    <label className="gts-field">
                      <span>Email <span className="gts-required">*</span></span>
                      <input type="email" name="email" value={form.email} onChange={onChange}/>
                      {errors.email && <em className="gts-error">{errors.email}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Home Phone</span>
                      <input name="homePhone" value={form.homePhone} onChange={onChange} maxLength={14}/>
                      {errors.homePhone && <em className="gts-error">{errors.homePhone}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Work Phone</span>
                      <input name="workPhone" value={form.workPhone} onChange={onChange} maxLength={14}/>
                      {errors.workPhone && <em className="gts-error">{errors.workPhone}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Cell/Other Phone</span>
                      <input name="cellPhone" value={form.cellPhone} onChange={onChange} maxLength={14}/>
                      {errors.cellPhone && <em className="gts-error">{errors.cellPhone}</em>}
                    </label>

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="okToLeaveMsg" checked={form.okToLeaveMsg} onChange={onChange}/>
                      <span>It’s okay to leave a message about appointments/benefits</span>
                    </label>
                  </div>
                </div>

                {/* Address */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Address</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Are you homeless?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input type="radio" name="homeless" value={opt} checked={form.homeless===opt} onChange={onChange}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Need an EBT card?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input type="radio" name="needEBT" value={opt} checked={form.needEBT===opt} onChange={onChange}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <label className="gts-field gts-span-2">
                      <span>Home Street {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeStreet" value={form.homeStreet} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>City {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeCity" value={form.homeCity} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>State (2) {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeState" value={form.homeState} onChange={onChange} maxLength={2} placeholder="TN"/>
                      {errors.homeState && <em className="gts-error">{errors.homeState}</em>}
                    </label>
                    <label className="gts-field">
                      <span>ZIP (5) {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeZip" value={form.homeZip} onChange={onChange} maxLength={5}/>
                      {errors.homeZip && <em className="gts-error">{errors.homeZip}</em>}
                    </label>
                    {errors.address && <em className="gts-error">{errors.address}</em>}

                    <label className="gts-field gts-span-2">
                      <span>Mailing Street (if different)</span>
                      <input name="mailingStreet" value={form.mailingStreet} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>Mailing City</span>
                      <input name="mailingCity" value={form.mailingCity} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>Mailing State (2)</span>
                      <input name="mailingState" value={form.mailingState} onChange={onChange} maxLength={2}/>
                      {errors.mailingState && <em className="gts-error">{errors.mailingState}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Mailing ZIP (5)</span>
                      <input name="mailingZip" value={form.mailingZip} onChange={onChange} maxLength={5}/>
                      {errors.mailingZip && <em className="gts-error">{errors.mailingZip}</em>}
                    </label>
                  </div>
                </div>

                {/* Demographics */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Demographics (Optional)</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Marital Status</span>
                      <select name="maritalStatus" value={form.maritalStatus} onChange={onChange}>
                        <option value="">Select…</option>
                        <option value="married">Married</option>
                        <option value="single">Single</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                        <option value="separated">Separated</option>
                      </select>
                    </label>

                    <fieldset className="gts-field gts-span-2">
                      <legend>Race (check all that apply)</legend>
                      <div className="gts-radio-list">
                        <label className="gts-radio"><input type="checkbox" name="raceWhite" checked={form.raceWhite} onChange={onChange}/><span>White</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceBlack" checked={form.raceBlack} onChange={onChange}/><span>Black/African American</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceAsian" checked={form.raceAsian} onChange={onChange}/><span>Asian</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceHawaiian" checked={form.raceHawaiian} onChange={onChange}/><span>Native Hawaiian/Other Pacific Islander</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceNative" checked={form.raceNative} onChange={onChange}/><span>American Indian/Alaskan Native</span></label>
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Families First–specific options */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Program Permissions</h5></div>
                  <div className="gts-form-grid">
                    {form.applyFamiliesFirst && (
                      <>
                        <fieldset className="gts-field">
                          <legend>Willing to comply with child support? (Families First)</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map((opt) => (
                              <label key={opt} className="gts-radio">
                                <input type="radio" name="willComplyChildSupport" value={opt} checked={form.willComplyChildSupport===opt} onChange={onChange}/>
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                          {errors.willComplyChildSupport && <em className="gts-error">{errors.willComplyChildSupport}</em>}
                        </fieldset>

                        <label className="gts-field gts-span-2 gts-checkboxline">
                          <input type="checkbox" name="consentSchoolAttendance" checked={form.consentSchoolAttendance} onChange={onChange}/>
                          <span>Permission to release school attendance records to DHS (Families First only)</span>
                        </label>
                      </>
                    )}

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="declineBooksFromBirth" checked={form.declineBooksFromBirth} onChange={onChange}/>
                      <span>Check to decline “Books from Birth” info sharing</span>
                    </label>

                    <fieldset className="gts-field">
                      <legend>Would you like a copy of your completed application?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input type="radio" name="wantCopy" value={opt} checked={form.wantCopy===opt} onChange={onChange}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.wantCopy === 'Yes' && (
                      <label className="gts-field">
                        <span>Preferred Copy Type</span>
                        <select name="copyType" value={form.copyType} onChange={onChange}>
                          <option value="Electronic">Electronic</option>
                          <option value="Paper">Paper</option>
                        </select>
                      </label>
                    )}
                  </div>
                </div>

                {/* Authorized Representatives */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Authorized Representatives</h5><div className="gts-small-note">You may designate up to two.</div></div>
                  <div className="gts-form-grid">
                    {/* Rep 1 */}
                    <label className="gts-field gts-span-2">
                      <span>Representative #1 – Full Name</span>
                      <input name="rep1Name" value={form.rep1Name} onChange={onChange}/>
                    </label>
                    <fieldset className="gts-field gts-span-2">
                      <legend>Rep #1 Permissions</legend>
                      <div className="gts-radio-list">
                        <label className="gts-radio"><input type="checkbox" name="rep1MayApplySNAP" checked={form.rep1MayApplySNAP} onChange={onChange}/><span>May apply for SNAP for me</span></label>
                        <label className="gts-radio"><input type="checkbox" name="rep1MayApplyFF" checked={form.rep1MayApplyFF} onChange={onChange}/><span>May apply for Families First for me</span></label>
                        <label className="gts-radio"><input type="checkbox" name="rep1MayUseSNAP" checked={form.rep1MayUseSNAP} onChange={onChange}/><span>May use my SNAP benefits</span></label>
                        <label className="gts-radio"><input type="checkbox" name="rep1MayUseFF" checked={form.rep1MayUseFF} onChange={onChange}/><span>May use my Families First benefits</span></label>
                      </div>
                    </fieldset>

                    {/* Rep 2 */}
                    <label className="gts-field gts-span-2">
                      <span>Representative #2 – Full Name</span>
                      <input name="rep2Name" value={form.rep2Name} onChange={onChange}/>
                    </label>
                    <fieldset className="gts-field gts-span-2">
                      <legend>Rep #2 Permissions</legend>
                      <div className="gts-radio-list">
                        <label className="gts-radio"><input type="checkbox" name="rep2MayApplySNAP" checked={form.rep2MayApplySNAP} onChange={onChange}/><span>May apply for SNAP for me</span></label>
                        <label className="gts-radio"><input type="checkbox" name="rep2MayApplyFF" checked={form.rep2MayApplyFF} onChange={onChange}/><span>May apply for Families First for me</span></label>
                        <label className="gts-radio"><input type="checkbox" name="rep2MayUseSNAP" checked={form.rep2MayUseSNAP} onChange={onChange}/><span>May use my SNAP benefits</span></label>
                        <label className="gts-radio"><input type="checkbox" name="rep2MayUseFF" checked={form.rep2MayUseFF} onChange={onChange}/><span>May use my Families First benefits</span></label>
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Voter Registration */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Voter Registration</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Are you registered to vote where you live now?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input type="radio" name="vrRegistered" value={opt} checked={form.vrRegistered===opt} onChange={onChange}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Would you like to register to vote?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input type="radio" name="vrWouldLikeToRegister" value={opt} checked={form.vrWouldLikeToRegister===opt} onChange={onChange}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Mail a voter registration form to you?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input type="radio" name="vrMailForm" value={opt} checked={form.vrMailForm===opt} onChange={onChange}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Federal/State Convictions (eligibility flags) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Program Integrity Questions</h5></div>
                  <div className="gts-form-grid">
                    {[
                      ['dvTwoStatesSameTime','Found guilty of receiving TANF or SNAP in two or more states at the same time?'],
                      ['dvTANFRestrictedLocations','Used TANF funds at restricted locations (e.g., liquor stores, casinos, adult entertainment, etc.)? (TANF only)'],
                      ['dvSellSNAPOver500','Convicted of buying or selling SNAP benefits over $500?'],
                      ['dvDrugFelonyPost1996','Felony conviction related to controlled substances after 08/22/1996? (SNAP/TANF)'],
                      ['dvFleeingFelonParoleProbation','Fleeing felon, or violating parole/probation?'],
                      ['dvTradeSNAPForDrugs','Convicted of trading SNAP for drugs?'],
                      ['dvReceivedSnapTanfOtherState30Days','Anyone applying received SNAP/TANF from another state in last 30 days?'],
                      ['dvAge18to24InStateCustody','If age 18–24, ever in state custody as a child?'],
                    ].map(([key, label]) => (
                      <fieldset className="gts-field" key={key}>
                        <legend>{label}</legend>
                        <div className="gts-radio-list">
                          {['Yes','No'].map((opt) => (
                            <label key={opt} className="gts-radio">
                              <input type="radio" name={key} value={opt} checked={form[key]===opt} onChange={onChange}/>
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                </div>

                {/* Household Income */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Household Income</h5><div className="gts-small-note">Monthly amounts before taxes/expenses</div></div>
                  <div className="gts-panel">
                    {incomeRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field">
                          <span>Person with income</span>
                          <input value={row.person || ''} onChange={(e)=>updateRow(incomeRows,setIncomeRows,idx,'person',e.target.value)}/>
                        </label>
                        <label className="gts-field">
                          <span>Source (job, Social Security, child support, etc.)</span>
                          <input value={row.source || ''} onChange={(e)=>updateRow(incomeRows,setIncomeRows,idx,'source',e.target.value)}/>
                        </label>
                        <label className="gts-field">
                          <span>Monthly amount ($)</span>
                          <input value={row.monthlyAmount || ''} onChange={(e)=>updateRow(incomeRows,setIncomeRows,idx,'monthlyAmount',e.target.value)}/>
                        </label>
                        <div className="gts-field" style={{ alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(incomeRows,setIncomeRows,idx)}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(incomeRows,setIncomeRows,{person:'',source:'',monthlyAmount:''})}>Add Income</button>
                    </div>
                  </div>
                </div>

                {/* Resources */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Household Resources</h5><div className="gts-small-note">Cash, bank accounts, CDs, stocks/bonds, retirement, trusts, annuities, etc.</div></div>
                  <div className="gts-panel">
                    {resourceRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field">
                          <span>Type</span>
                          <input value={row.type || ''} onChange={(e)=>updateRow(resourceRows,setResourceRows,idx,'type',e.target.value)}/>
                        </label>
                        <label className="gts-field">
                          <span>Value ($)</span>
                          <input value={row.value || ''} onChange={(e)=>updateRow(resourceRows,setResourceRows,idx,'value',e.target.value)}/>
                        </label>
                        <div className="gts-field" style={{ alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(resourceRows,setResourceRows,idx)}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(resourceRows,setResourceRows,{type:'',value:''})}>Add Resource</button>
                    </div>
                  </div>
                </div>

                {/* Expenses */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Expenses</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Child Care – Amount per week ($)</span>
                      <input name="childCareAmountWeekly" value={form.childCareAmountWeekly} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>Child Care Provider Name</span>
                      <input name="childCareProvider" value={form.childCareProvider} onChange={onChange}/>
                    </label>

                    <label className="gts-field">
                      <span>Rent / Mortgage — Monthly ($)</span>
                      <input name="shelterRentOrMortgage" value={form.shelterRentOrMortgage} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>Gas/Electric — Monthly ($)</span>
                      <input name="shelterGasElectricMonthly" value={form.shelterGasElectricMonthly} onChange={onChange}/>
                    </label>
                    <label className="gts-field">
                      <span>Phone — Monthly ($)</span>
                      <input name="shelterPhoneMonthly" value={form.shelterPhoneMonthly} onChange={onChange}/>
                    </label>
                  </div>

                  {/* Medical recurring (elderly/disabled can increase SNAP) */}
                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Recurring Medical Expenses</div>
                    {medicalRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field">
                          <span>Type (e.g., prescriptions, premiums)</span>
                          <input value={row.type || ''} onChange={(e)=>updateRow(medicalRows,setMedicalRows,idx,'type',e.target.value)}/>
                        </label>
                        <label className="gts-field">
                          <span>Monthly amount ($)</span>
                          <input value={row.monthlyAmount || ''} onChange={(e)=>updateRow(medicalRows,setMedicalRows,idx,'monthlyAmount',e.target.value)}/>
                        </label>
                        <div className="gts-field" style={{ alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(medicalRows,setMedicalRows,idx)}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(medicalRows,setMedicalRows,{type:'',monthlyAmount:''})}>Add Medical Expense</button>
                    </div>
                  </div>

                  {/* Child support paid */}
                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Child Support Paid (legally obligated)</div>
                    {childSupportRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field">
                          <span>Child</span>
                          <input value={row.child || ''} onChange={(e)=>updateRow(childSupportRows,setChildSupportRows,idx,'child',e.target.value)}/>
                        </label>
                        <label className="gts-field">
                          <span>Monthly amount ($)</span>
                          <input value={row.monthlyAmount || ''} onChange={(e)=>updateRow(childSupportRows,setChildSupportRows,idx,'monthlyAmount',e.target.value)}/>
                        </label>
                        <div className="gts-field" style={{ alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(childSupportRows,setChildSupportRows,idx)}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(childSupportRows,setChildSupportRows,{child:'',monthlyAmount:''})}>Add Child Support</button>
                    </div>
                  </div>
                </div>

                {/* Declaration & Signature */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange}/>
                      <span>
                        I certify the information provided is true and complete and understand program rules and penalties for fraud.
                        <span className="gts-required"> *</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Applicant Signature (type full name) <span className="gts-required">*</span></span>
                      <input name="signature" value={form.signature} onChange={onChange}/>
                      {errors.signature && <em className="gts-error">{errors.signature}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Date <span className="gts-required">*</span></span>
                      <input type="date" name="signedDate" value={form.signedDate} onChange={onChange}/>
                      {errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}
                    </label>
                  </div>
                </div>

                {/* Actions (spacing class to keep off the signature box) */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <a 
                    href="/guides/StateOfGTS-FamilyAssistanceApplication-2025V1.pdf" 
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download Application (PDF)
                  </a>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FamilyAssistanceApplication;
